import { Component } from '@theme/component';
import {
  clamp,
  getVisibleElements,
  isMobileBreakpoint,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

// Sub-pixel slack when comparing a scroll position against its extremes.
const SCROLL_EDGE_TOLERANCE = 1;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement} [progressBar]
 * @property {HTMLElement[]} [progressBarSegments]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  updatedCallback() {
    super.updatedCallback();

    // Slides can be added or hidden without the scroller moving (variant swaps,
    // the bundle gallery rebuilding its combination slide), which changes the
    // scrollable extent the fill is measured against.
    this.#updateProgressBar();
  }

  async connectedCallback() {
    super.connectedCallback();

    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    // A slideshow that has already been set up is here because it was moved, not
    // rendered: relocating a node runs `disconnectedCallback` and then this, and
    // morph moves nodes whenever it reorders siblings (a variant swap that shifts
    // the media order does it). Only the torn-down listeners are rebound — the
    // one-time setup must not run again, or the visitor is thrown back to the
    // initial slide mid-browse.
    if (this.#scroll) {
      this.#bindListeners();
      return;
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount <= 1) {
      this.#setupSlideshowWithoutControls();
      return;
    }

    if (this.#shouldDeferSetup()) {
      this.#prepareDeferredSetup();
      this.#deferSetupUntilVisible();
      return;
    }

    this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#unbindListeners();

    if (this.#deferredSetupObserver) {
      this.#deferredSetupObserver.disconnect();
      this.#deferredSetupObserver = null;
    }
  }

  /**
   * Attaches everything that reacts to the live DOM: the scroll listener that
   * keeps `current` (and with it the pagination) in step with the scroller, the
   * unthrottled one that scrubs the progress bar, pointer dragging, and
   * autoplay's hover and visibility pauses.
   *
   * Split out of `#setupSlideshow` so a reconnect can rebind without repeating
   * the one-time initialization.
   */
  #bindListeners() {
    this.#unbindListeners();

    const { scroller } = this.refs;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    // Separate from the `Scroller` above, whose callback is throttled to the
    // scroll-end timeout: at that rate the bar advances in visible steps.
    scroller.addEventListener('scroll', this.#handleProgressScroll, { passive: true });
    this.#updateProgressBar();

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.refs.slideshowContainer) this.#resizeObserver?.observe(this.refs.slideshowContainer);
  }

  /**
   * Detaches everything `#bindListeners` attached. Removals are unconditional:
   * the slide count and the refs can both change while the element is connected,
   * so gating teardown on them leaks listeners.
   */
  #unbindListeners() {
    this.#scroll?.destroy();
    this.refs.scroller?.removeEventListener('scroll', this.#handleProgressScroll);
    this.refs.scroller?.removeEventListener('mousedown', this.#handleMouseDown);

    if (this.#progressFrame !== null) cancelAnimationFrame(this.#progressFrame);
    this.#progressFrame = null;

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#resizeObserver?.disconnect();
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  select(input, event, options = {}) {
    if (!this.refs.slides?.length) return;
    if (!this.#scroll) return;
    if (this.#wrapping) return;

    // Clear any previously revealed slides
    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Resolve the requested index from the input
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find(
          (slide) => slide.getAttribute('slide-id') == input.id,
        );

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { slides } = this;

    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    // Seamless infinite loop: when crossing a boundary sequentially, keep
    // scrolling in the same direction into a temporary clone of the destination
    // slide rather than jumping back to the opposite end.
    if (this.seamlessLoop && this.#initialized && !prefersReducedMotion()) {
      const lastIndex = slides.length - 1;
      if (requestedIndex > lastIndex && this.current === lastIndex) {
        this.#seamlessWrap('forward', event);
        return;
      }
      if (requestedIndex < 0 && this.current === 0) {
        this.#seamlessWrap('backward', event);
        return;
      }
    }

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Wrap for infinite mode
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const slide = slides[index];
    if (!slide) return;

    // Don't re-select the same slide
    if (index === this.current && this.#initialized) return;

    const previousIndex = this.current;
    const instant = prefersReducedMotion() || !animate;

    slide.setAttribute('aria-hidden', 'false');

    this.#scroll.to(slide, { instant });

    this.current = index;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Performs a seamless infinite-loop transition across a boundary.
   *
   * Instead of jumping back to the opposite end, this animates into a temporary
   * clone of the destination slide placed adjacent to the current slide, then
   * instantly repositions onto the real slide. Because the clone is visually
   * identical to its source, the motion reads as a single continuous scroll in
   * one direction (right-to-left for `forward`, left-to-right for `backward`).
   *
   * @param {'forward'|'backward'} direction - The direction to wrap.
   * @param {Event} [event] - The event that triggered the wrap, if any.
   */
  async #seamlessWrap(direction, event) {
    const { slides } = this;
    const { scroller } = this.refs;
    if (this.#wrapping || !slides?.length || !scroller || !this.#scroll) return;

    const lastIndex = slides.length - 1;
    const targetIndex = direction === 'forward' ? 0 : lastIndex;
    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    this.#wrapping = true;
    this.setAttribute('transitioning', '');
    const previousIndex = this.current;

    // Build a non-interactive clone of the destination slide. Stripping `ref`
    // keeps it out of `refs.slides`, and removing `slide-id` keeps it out of
    // `this.slides`, so indices and controls stay correct.
    const clone = /** @type {HTMLElement} */ (targetSlide.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('id');
    clone.removeAttribute('on:click');
    clone.setAttribute('aria-hidden', 'false');
    clone.setAttribute('data-slideshow-clone', '');
    clone.style.pointerEvents = 'none';
    for (const media of clone.querySelectorAll('video, model-viewer, iframe')) media.remove();

    this.#scroll.snap = false;

    if (direction === 'forward') {
      scroller.appendChild(clone);
    } else {
      // The slide we're leaving; keep it visually fixed after the leading clone
      // shifts every following slide to the right.
      const originSlide = slides[this.current] ?? targetSlide;
      scroller.insertBefore(clone, scroller.firstChild);
      // Re-anchor instantly onto the origin slide. A direct `scrollLeft`
      // assignment would inherit the CSS `scroll-behavior: smooth` and animate
      // the correction (making the destination slide pop in instead of slide),
      // so route through the Scroller which forces an instant jump.
      this.#scroll.to(originSlide, { instant: true });
    }

    this.#scroll.to(clone);
    await this.#scroll.finished;

    if (!this.isConnected) {
      clone.remove();
      this.#wrapping = false;
      this.removeAttribute('transitioning');
      return;
    }

    // Land on the real destination slide, which is pixel-identical to the clone.
    // Repositioning both before and after removing the clone keeps the viewport
    // stable: the first call moves onto the real slide while the clone still
    // occupies space, the second corrects for the layout shift its removal causes
    // (the browser also re-clamps scrollLeft when content shrinks).
    this.#scroll.to(targetSlide, { instant: true });
    clone.remove();
    this.#scroll.to(targetSlide, { instant: true });

    this.current = targetIndex;
    this.#updateVisibleSlides();
    this.#centerSelectedThumbnail(targetIndex);

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: targetIndex,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide: targetSlide,
        id: targetSlide.getAttribute('slide-id'),
      }),
    );

    this.#scroll.snap = true;
    this.removeAttribute('transitioning');
    this.#wrapping = false;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval) {
    if (typeof interval !== 'number') interval = this.autoplayInterval;
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if ((this.matches(':hover') && !isMobileBreakpoint()) || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, progressBarSegments, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots, progressBarSegments]) {
      controls?.forEach((el, i) => el.setAttribute('aria-current', i === value ? 'true' : 'false'));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  /**
   * Whether the slideshow loops seamlessly: crossing a boundary sequentially
   * continues scrolling in the same direction (via a temporary clone) instead
   * of jumping back to the opposite end.
   * @type {boolean}
   */
  get seamlessLoop() {
    return this.hasAttribute('seamless-loop');
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' ||
      (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /** Whether the slideshow has been initialized with its first select call. */
  #initialized = false;

  /** Whether a seamless boundary wrap animation is currently in progress. */
  #wrapping = false;

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number|null} Pending rAF handle for the progress bar update. */
  #progressFrame = null;

  /** @type {Scroller} */
  #scroll;

  /** @type {ResizeObserver} */
  #resizeObserver;

  /** @type {IntersectionObserver | null} */
  #deferredSetupObserver = null;

  #shouldDeferSetup() {
    return this.getAttribute('data-defer-slideshow-init') === 'true';
  }

  #prepareDeferredSetup() {
    this.current = this.initialSlideIndex;
    const initialSlide = this.refs.slides?.[this.initialSlideIndex] || this.refs.slides?.[0];
    if (initialSlide) {
      initialSlide.setAttribute('aria-hidden', 'false');
    }
  }

  #deferSetupUntilVisible() {
    if (this.#deferredSetupObserver) return;
    this.#deferredSetupObserver = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible) return;

        this.#deferredSetupObserver?.disconnect();
        this.#deferredSetupObserver = null;

        if (!this.isConnected || this.#scroll) return;
        this.#setupSlideshow();
      },
      {
        root: null,
        rootMargin: '320px 0px',
        threshold: 0,
      },
    );
    this.#deferredSetupObserver.observe(this);
  }

  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  #setupSlideshow() {
    if (this.#scroll) return;

    this.#bindListeners();

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }

        this.#initialized = true;
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        // A resize changes the scroller's extents without scrolling it, so the
        // fill has to be recomputed against the new metrics.
        this.#updateProgressBar();

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );
  };

  /**
   * Coalesces the raw scroll events onto one progress bar write per frame.
   */
  #handleProgressScroll = () => {
    if (this.#progressFrame !== null) return;

    this.#progressFrame = requestAnimationFrame(() => {
      this.#progressFrame = null;
      this.#updateProgressBar();
    });
  };

  /**
   * Scrubs the progress bar from the scroller's position rather than from
   * `current`.
   *
   * An index-derived fill can't be smooth here. `current` is whichever slide
   * sits nearest the scroller's centre, so a carousel showing more than one
   * slide at a time skips indices outright and steps several at once — the fill
   * then leaps a segment or more per update and animates on its own timer,
   * detached from the finger.
   *
   * The revealed fraction of the strip is exactly proportional to the scroll
   * offset, so it tracks drags and smooth scrolls frame for frame. A single-up
   * carousel still rests on n/total at every snap point, matching the
   * segment-per-slide fill this replaces.
   */
  #updateProgressBar() {
    const { progressBar, scroller } = this.refs;
    if (!progressBar || !scroller) return;

    const { scrollWidth, clientWidth } = scroller;
    if (scrollWidth <= 0) return;

    // `Math.abs` covers RTL, where scrollLeft runs from 0 down to -maxScroll.
    const revealed = Math.abs(scroller.scrollLeft) + clientWidth;

    progressBar.style.setProperty('--progress', String(clamp(revealed / scrollWidth, 0, 1)));
  }

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    // Programmatic scrolls (arrow clicks, `select`) never reach #sync, so the
    // edges are re-checked here too: a step that overshoots the last full page
    // clamps the scroll at its maximum without the requested slide reaching the
    // snap point, which would otherwise leave `current` short of the last slide.
    const edgeIndex = this.#edgeIndex();
    if (edgeIndex !== null && edgeIndex !== this.current) this.current = edgeIndex;

    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * The slide index to report while the scroller rests at one of its scroll
   * extremes, or `null` when it sits somewhere in between.
   *
   * Slides narrower than the scroller (multi-up carousels) can't bring the last
   * slide to the snap point, so matching the closest slide stops short of it and
   * neither the first nor the last slide is ever reported as current — leaving
   * pagination unfilled and the arrows active on the real edges of the track.
   *
   * @returns {number|null} The pinned index, or null when not at an extreme.
   */
  #edgeIndex() {
    const { slides } = this;
    const { scroller } = this.refs;
    if (!slides?.length || !scroller) return null;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= SCROLL_EDGE_TOLERANCE) return null;

    // `Math.abs` covers RTL, where scrollLeft runs from 0 down to -maxScroll.
    const position = Math.abs(scroller.scrollLeft);
    if (position <= SCROLL_EDGE_TOLERANCE) return 0;
    if (position >= maxScroll - SCROLL_EDGE_TOLERANCE) return slides.length - 1;

    return null;
  }

  /**
   * Determines the current slide by finding which slide is closest to the
   * scroller's snap alignment point (scrollLeft + scroll-padding).
   * This works correctly for both scroll-snap-align: start and center.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides?.length) return (this.current = 0);
    if (!this.#scroll) return (this.current = 0);

    const edgeIndex = this.#edgeIndex();
    if (edgeIndex !== null) return (this.current = edgeIndex);

    const { scroller } = this.refs;
    const scrollerRect = scroller.getBoundingClientRect();

    let closestIndex = 0;
    let closestDistance = Infinity;

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (!slide) continue;
      const slideRect = slide.getBoundingClientRect();

      // Calculate the slide's center relative to the scroller's center
      const slideCenter = slideRect.left + slideRect.width / 2;
      const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
      const distance = Math.abs(slideCenter - scrollerCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    return (this.current = closestIndex);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const synced = this.#sync();

      // If the user flicked with enough velocity, advance one slide in that direction
      const modifier =
        synced !== this.current || Math.abs(velocity) < 10 || distanceTravelled < 10
          ? 0
          : direction;
      const lastIndex = slides.length - 1;
      let newIndex = synced + modifier;
      if (this.infinite) {
        if (newIndex < 0) newIndex = lastIndex;
        else if (newIndex > lastIndex) newIndex = 0;
      } else {
        newIndex = clamp(newIndex, 0, lastIndex);
      }

      const newSlide = slides[newIndex];

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.select(newIndex, event, { animate: true });

      this.#centerSelectedThumbnail(newIndex);

      await this.#scroll.finished;

      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'),
    );
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    scheduler.schedule(() => {
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
